import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { ErrorCodes, withErrorCode } from '../../api/errorCodes';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface EmailFormProps {
  loading: boolean;
  onSubmit: (email: string) => void;
}

export function EmailForm({ loading, onSubmit }: EmailFormProps) {
  const { t } = useTranslation('auth');
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [invalid, setInvalid] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    if (!EMAIL_RE.test(trimmed)) {
      setInvalid(true);
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit} noValidate>
      <TextField
        label={t('emailLabel')}
        placeholder={t('emailPlaceholder')}
        type="email"
        value={email}
        onChange={e => {
          setEmail(e.target.value);
          setInvalid(false);
        }}
        error={invalid}
        helperText={
          invalid
            ? withErrorCode(t('invalidEmail'), ErrorCodes.signInInvalidEmail)
            : undefined
        }
        disabled={loading}
        variant="outlined"
        fullWidth
        inputProps={{ 'data-testid': 'email-input' }}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={loading || !email.trim()}
        data-testid="submit-button"
      >
        {loading ? t('submitting') : t('submit')}
      </Button>
    </form>
  );
}
